import React, { FC, useEffect, useMemo, useState }    from 'react';
import { Button, Icon, Message }                       from 'semantic-ui-react';
import { Flexbox, StyledContainer }                    from '@src/views/containers';
import { FlexAlign, FlexJustify }                      from '@src/types';
import { useFeatureFlags, useSystemModals }            from '@src/hooks';
import { useCentralCalls }                             from '@src/rest/CentralCalls';
import DashGrid, { DashGridItem }                      from '@src/views/pages/projects/dash/DashGrid';
import type { DashPage }                               from '@src/views/pages/projects/dash/DashContainerPage';
import { DashScope, DashScopeContext, DashScopeValue } from '@src/views/pages/projects/dash/DashScopeContext';
import { CreateDashPageDialog }                        from '@src/views/pages/projects/dash/operations/CreateDashPageDialog';
import { DeleteDashPageDialog }                        from '@src/views/pages/projects/dash/operations/DeleteDashPageDialog';
import { readStoredString, writeStoredString }         from './consultantDashStorage';

const DEFAULT_PAGE_ICON = 'chart bar';

interface ConsultantDashPage extends DashPage {
    sortOrder: number | null;
}

export interface ConsultantScopedDashboardProps {
    scope: DashScope;
    params: Record<string, string>;
    title: string;
    pageStorageKey: string;
    gridIdentityKey: string;
    emptyMessage?: string;
}

const parsePageConfig = ( config: unknown ): DashGridItem[] => {
    if( Array.isArray( config ) ) return config as DashGridItem[];
    if( typeof config !== 'string' || !config ) return [];

    try {
        const parsed: unknown = JSON.parse( config );
        return Array.isArray( parsed ) ? parsed as DashGridItem[] : [];
    } catch {
        return [];
    }
};

const byPageOrder = ( a: ConsultantDashPage, b: ConsultantDashPage ): number =>
    ( a.sortOrder ?? Number.MAX_SAFE_INTEGER ) - ( b.sortOrder ?? Number.MAX_SAFE_INTEGER )
    || a.name.localeCompare( b.name );

/**
 * DashGrid host for Consultant Dash. Pages and widgets come from DashPage / DashProcedure
 * rows matching `scope`; every widget procedure receives `params` (clientId or consultantId).
 */
const ConsultantScopedDashboard: FC<ConsultantScopedDashboardProps> = ( {
    scope,
    params,
    title,
    pageStorageKey,
    gridIdentityKey,
    emptyMessage = 'No dashboard pages have been set up yet.',
} ) => {
    const { TableSelect, RunUpdate } = useCentralCalls(),
          { enableReportsAdmin }     = useFeatureFlags(),
          modalActions               = useSystemModals();

    const [ pages, setPages ]               = useState<ConsultantDashPage[]>( [] ),
          [ activePageId, setActivePageId ] = useState<string>( () => readStoredString( pageStorageKey, '' ) ),
          [ loading, setLoading ]           = useState<boolean>( true ),
          [ loadError, setLoadError ]       = useState<string>( '' ),
          [ editMode, setEditMode ]         = useState<boolean>( false ),
          [ refreshKey, setRefreshKey ]     = useState<number>( 0 );

    useEffect( () => {
        let cancelled = false;

        const loadPages = async (): Promise<void> => {
            setLoading( true );
            setLoadError( '' );

            try {
                const rows: ConsultantDashPage[] | null = await TableSelect( 'DashPage', { Hidden: false, Scope: scope } );
                if( cancelled ) return;

                setPages(
                    ( rows ?? [] )
                        .map( row => ( { ...row, pageConfig: parsePageConfig( row.pageConfig ) } ) )
                        .sort( byPageOrder )
                );
            } catch( e ) {
                console.error( 'Failed to load dashboard pages', e );
                if( !cancelled ) setLoadError( 'The dashboard could not be loaded.' );
            } finally {
                if( !cancelled ) setLoading( false );
            }
        };

        loadPages();

        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ refreshKey, scope ] );

    const activePage: ConsultantDashPage | null = pages.find( page => page.id === activePageId ) ?? pages[ 0 ] ?? null;

    const paramsKey = JSON.stringify( params );
    const scopeValue = useMemo<DashScopeValue>(
        () => ( { scope, params } ),
        // Callers often pass a fresh object; compare by value so widgets do not refetch every render.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [ scope, paramsKey ]
    );

    const handleSelectPage = ( pageId: string ): void => {
        setActivePageId( pageId );
        writeStoredString( pageStorageKey, pageId );
    };

    const handleLayoutSaved = ( pageId: string, pageConfig: DashGridItem[] ): void => {
        setPages( prev => prev.map( page => ( page.id === pageId ? { ...page, pageConfig } : page ) ) );
    };

    const handleOpenCreatePage = (): void => {
        modalActions.show(
            <CreateDashPageDialog
                scope={ scope }
                onPageCreated={ () => setRefreshKey( key => key + 1 ) }
                onClose={ () => modalActions.toggle( false ) }
            />,
            { size: 'tiny', centered: false }
        );
    };

    const handleOpenDeletePage = ( page: ConsultantDashPage ): void => {
        modalActions.show(
            <DeleteDashPageDialog
                page={ page }
                onConfirm={ async ( pageId: string ) => {
                    try {
                        await RunUpdate( 'DashPage', pageId, { Hidden: true } );
                        setPages( prev => prev.filter( p => p.id !== pageId ) );
                    } catch( e ) {
                        console.error( 'Failed to delete the dashboard page', e );
                    }
                } }
                onClose={ () => modalActions.toggle( false ) }
            />,
            { size: 'tiny', centered: false }
        );
    };

    const renderBody = (): React.ReactNode => {
        if( loading ){
            return (
                <p className={ 'document-version-empty' }>
                    <Icon name={ 'spinner' } loading /> Loading dashboard...
                </p>
            );
        }

        if( loadError ){
            return <Message negative icon={ 'warning circle' } content={ loadError }/>;
        }

        if( !activePage ){
            return (
                <p className={ 'consultant-widget-empty consultant-dash-empty-state' }>
                    { emptyMessage }
                    { enableReportsAdmin && ' Use the + button above to create one.' }
                </p>
            );
        }

        return (
            <DashScopeContext.Provider value={ scopeValue }>
                <DashGrid
                    key={ `${ activePage.id }:${ gridIdentityKey }` }
                    pageJson={ activePage.pageConfig }
                    editMode={ editMode }
                    dashPageId={ activePage.id }
                    onExitEditMode={ () => setEditMode( false ) }
                    onLayoutSaved={ pageConfig => handleLayoutSaved( activePage.id, pageConfig ) }
                />
            </DashScopeContext.Provider>
        );
    };

    return (
        <StyledContainer className={ 'consultant-dashboard' }>
            <Flexbox align={ FlexAlign.CENTER } className={ 'client-content-header' }>
                <Flexbox align={ FlexAlign.CENTER } justify={ FlexJustify.START } className={ 'client-content-header-title' }>
                    <h3 className={ 'client-content-title' }>{ title }</h3>

                    { enableReportsAdmin && (
                        <span className={ 'client-content-header-actions' } role={ 'toolbar' } aria-label={ 'Dashboard actions' }>
                            <Button
                                type={ 'button' }
                                basic
                                compact
                                icon
                                size={ 'mini' }
                                active={ editMode }
                                disabled={ !activePage }
                                className={ 'client-content-header-action-btn' }
                                aria-label={ editMode ? 'Finish editing the layout' : 'Edit the layout' }
                                title={ editMode ? 'Finish editing the layout' : 'Edit the layout' }
                                onClick={ () => setEditMode( prev => !prev ) }
                            >
                                <Icon name={ editMode ? 'check' : 'pencil' }/>
                            </Button>
                            <Button
                                type={ 'button' }
                                basic
                                compact
                                icon
                                size={ 'mini' }
                                disabled={ editMode }
                                className={ 'client-content-header-action-btn' }
                                aria-label={ 'New dashboard page' }
                                title={ 'New dashboard page' }
                                onClick={ handleOpenCreatePage }
                            >
                                <Icon name={ 'plus' }/>
                            </Button>
                            <Button
                                type={ 'button' }
                                basic
                                compact
                                icon
                                size={ 'mini' }
                                disabled={ editMode || !activePage }
                                className={ 'client-content-header-action-btn' }
                                aria-label={ 'Delete this dashboard page' }
                                title={ 'Delete this dashboard page' }
                                onClick={ () => activePage && handleOpenDeletePage( activePage ) }
                            >
                                <Icon name={ 'trash alternate outline' }/>
                            </Button>
                        </span>
                    ) }
                </Flexbox>

                { pages.length > 0 && (
                    <Flexbox align={ FlexAlign.CENTER } justify={ FlexJustify.END } className={ 'consultant-dashboard-pages' }>
                        { pages.map( page => (
                            <Button
                                key={ page.id }
                                type={ 'button' }
                                size={ 'small' }
                                icon={ page.icon || DEFAULT_PAGE_ICON }
                                content={ page.name }
                                title={ page.description || page.name }
                                aria-pressed={ page.id === activePage?.id }
                                disabled={ editMode }
                                className={ `consultant-dashboard-page-btn ${ page.id === activePage?.id ? 'is-active' : '' }` }
                                onClick={ () => handleSelectPage( page.id ) }
                            />
                        ) ) }
                    </Flexbox>
                ) }
            </Flexbox>

            { renderBody() }
        </StyledContainer>
    );
};

export default ConsultantScopedDashboard;
